import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  Switch,
  Chip,
  Typography,
  Box,
  TextField,
  IconButton
} from '@mui/material';
import { Sparklines, SparklinesBars } from 'react-sparklines';
import { ArrowDropUp, ArrowDropDown, Edit, Save, Cancel } from '@mui/icons-material';
import { humanReadableDate } from '../utils/massageDataUtil';
import { Link } from 'react-router-dom';
import { CustomWidthTooltipNoImage } from '../utils/customWidthToolTipUtilNoImage';
import { getSeverityColor } from '../utils/getSeverityColor';

/**
 * EditableNotesCell Component
 * Handles inline editing of notes with save/cancel functionality
 */
const EditableNotesCell = ({ item, onNotesUpdate, itemKey = 'no' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(item.notes || '');
  const [isSaving, setIsSaving] = useState(false);

  // Reset edit value when item changes
  useEffect(() => {
    setEditValue(item.notes || '');
  }, [item.notes]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditValue(item.notes || '');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(item.notes || '');
  };

  const handleSave = async () => {
    if (editValue === item.notes) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onNotesUpdate(item, editValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save notes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 300 }}>
        <TextField
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyPress}
          multiline
          minRows={1}
          maxRows={4}
          size="small"
          variant="outlined"
          placeholder="Enter notes..."
          disabled={isSaving}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#3A3A3A',
              color: 'white',
              fontSize: '0.75rem',
              '& fieldset': {
                borderColor: '#555',
              },
              '&:hover fieldset': {
                borderColor: '#777',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
            },
          }}
          autoFocus
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={handleSave}
            disabled={isSaving}
            sx={{ color: '#4CAF50', p: 0.5 }}
          >
            <Save fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleCancel}
            disabled={isSaving}
            sx={{ color: '#f44336', p: 0.5 }}
          >
            <Cancel fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 300 }}>
      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#FFFFFF', flex: 1 }}>
        {item.notes ? (
          item.notes.length > 100 
            ? `${item.notes.substring(0, 100)}...` 
            : item.notes
        ) : (
          <span style={{ color: '#888', fontStyle: 'italic' }}>Click to add notes</span>
        )}
      </Typography>
      <IconButton
        size="small"
        onClick={handleEdit}
        sx={{ color: '#1976d2', p: 0.5, opacity: 0.7, '&:hover': { opacity: 1 } }}
      >
        <Edit fontSize="small" />
      </IconButton>
    </Box>
  );
};

const UniversalDataTable = ({ 
  data, 
  columns, 
  onToggle, 
  onNotesUpdate, 
  sortConfig, 
  onSortChange,
  itemKey = 'no'
}) => {

  /**
   * Handle column header click for sorting
   * Toggles between ascending and descending order
   */
  const handleSort = (field) => {
    const direction =
      sortConfig.key === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key: field, direction });
  };

  /**
   * Sort Icon Component
   */
  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    );
  };

  /**
   * Render cell content based on column type
   */
  const renderCellContent = (item, column) => {
    const value = item[column.field];

    switch (column.type) {
      case 'text':
        return (
            column.field == 'sensor'
            ? <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#FFFFFF' , cursor:'pointer'}}>
              { value == 'AdminCore' ?
                 (<CustomWidthTooltipNoImage title={ 'Aggregates global data to identify high-level threats and patterns that individual sensors might miss.' } 
                    placement="top" dynamicWidth="250px" dyanmicHeight="50px">
                    {value}
                  </CustomWidthTooltipNoImage>)
                :  value == 'sensor-001' ?
                   (<CustomWidthTooltipNoImage title={ 'Analyzes traffic within a specific network segment to identify patterns and detect localized anomalies.' } 
                    placement="top" dynamicWidth="250px" dyanmicHeight="50px">
                  {value}
                  </CustomWidthTooltipNoImage>)
                :  value == 'shipper1' ?
                  (<CustomWidthTooltipNoImage title={ 'Monitors direct endpoint activity, providing visibility into the activities and events on this specific device.' } 
                    placement="top" dynamicWidth="250px" dyanmicHeight="50px">
                  {value}
                  </CustomWidthTooltipNoImage>)
                : (null)
              }
              </Typography>
            : <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#FFFFFF' }}>
              {value}
              </Typography>
    
          
        );

      case 'number':
        return (
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#FFFFFF' }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
        );

      case 'switch':
        return (
          <Switch 
            checked={!!value} 
            onChange={(e) => onToggle(item, column.field, e.target.checked)}
            size="small"
          />
        );

      case 'severity':
        return (
          <Chip 
            label={value} 
            size="small"
            sx={{ 
              backgroundColor: getSeverityColor(value),
              color: 'white',
              fontWeight: 'bold',
              width: '70px'
            }}
          />
        );

      case 'chip':
        return (
          <Chip 
            label={value} 
            size="small" 
            sx={{ 
              color: '#fff', 
              fontSize: '0.7rem',
              fontWeight: 500 
            }} 
          />
        );

      case 'sparkline':
        return value && Array.isArray(value) ? (
          <Sparklines data={value} width={100} height={20}>
            <SparklinesBars style={{ fill: "#f40004", fillOpacity: "1" }} />
          </Sparklines>
        ) : null;

      case 'notes':
        return onNotesUpdate ? (
          <EditableNotesCell 
            item={item} 
            onNotesUpdate={onNotesUpdate}
            itemKey={itemKey}
          />
        ) : (
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#FFFFFF', maxWidth: 300 }}>
            {value ? (
              value.length > 100 ? `${value.substring(0, 100)}...` : value
            ) : 'No notes'}
          </Typography>
        );

      case 'truncated':
        return (
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#FFFFFF', maxWidth: 300 }}>
            {value && value.length > 100 ? `${value.substring(0, 100)}...` : value}
          </Typography>
        );

      case 'timestamp':
        return (
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#FFFFFF' }}>
            {humanReadableDate(value)}            
          </Typography>
        );

      case 'link':
        return (
          <nav>          
              { value !== '' &&
                <Link 
                  to={value}
                  sx={{ 
                    ml: 5,
                    outline: 'none',
                    '&:focus': {
                      outline: 'none'
                    }
                  }} 
                  style={{ cursor: 'pointer', color: '#347deb' }} 
                  underline="hover"
                >
                  details
                </Link>
              }
          </nav>
        );

      default:
        return (
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#FFFFFF' }}>
            {value}
          </Typography>
        );
    }
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: '#0568AE' }}>
          {columns.map((column) => (
            <>
              {column.toolTip !== undefined ?
                  <CustomWidthTooltipNoImage title={ column.toolTip } placement="top" dynamicWidth="250px" dyanmicHeight="50px">
               
                  <TableCell
                    key={column.field}
                    onClick={column.sortable ? () => handleSort(column.field) : undefined}
                    sx={{
                      color: '#EEEEEE',
                      fontWeight: 'bold',
                      cursor: column.sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>{column.display}</span>
                      {column.sortable && <SortIcon field={column.field} />}
                    </Stack>
                  </TableCell>
              
                </CustomWidthTooltipNoImage> : 

                <TableCell
                    key={column.field}
                    onClick={column.sortable ? () => handleSort(column.field) : undefined}
                    sx={{
                      color: '#EEEEEE',
                      fontWeight: 'bold',
                      cursor: column.sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>{column.display}</span>
                      {column.sortable && <SortIcon field={column.field} />}
                    </Stack>
                  </TableCell>
              } 
            </>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map((item, index) => (
          <TableRow
            key={item[itemKey] || index}
            sx={{
              height: 30,
              backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#1A1A1A',
              '&:hover': {
                backgroundColor: '#333338',
              }
            }}
          >
            {columns.map((column) => (
              <TableCell key={column.field} sx={{ py: 1 }}>
                {renderCellContent(item, column)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// Column configurations for different table types

/**
 * Systems Table Columns Configuration
 */
export const SYSTEMS_COLUMNS = [
  { display: 'no', field: 'no', sortable: true, type: 'text' },
  { display: 'ip_dns', field: 'ip_dns', sortable: true, type: 'text' },
  { display: 'id', field: 'id', sortable: true, type: 'text' },
  { display: 'collector', field: 'collector', sortable: true, type: 'text' },
  { display: 'authorized', field: 'authorized', sortable: true, type: 'switch' },
  { display: 'notes', field: 'notes', sortable: false, type: 'notes' },
];

/**
 * Applications Table Columns Configuration
 */
export const APPLICATIONS_COLUMNS = [
  { display: 'No', field: 'no', sortable: true, type: 'text' },
  { display: 'Collector', field: 'collector', sortable: true, type: 'text' },
  { display: 'Name', field: 'name', sortable: true, type: 'text' },
  { display: 'Port', field: 'port', sortable: true, type: 'number' },
  { display: 'Authorized', field: 'authorized', sortable: true, type: 'switch' },
  { display: 'Notes', field: 'notes', sortable: false, type: 'notes' },
];

/**
 * Alerts Table Columns Configuration
 */
export const ALERTS_COLUMNS = [
  { display: 'Reporter', field: 'sensor', sortable: true, type: 'text', toolTip: 'The reporter attributes the event to its original source.'},
  { display: 'Events', field: 'events', sortable: true, type: 'number' },
  { display: 'Severity', field: 'severity', sortable: true, type: 'severity' },
  { display: 'First Seen', field: 'first_seen', sortable: true, type: 'timestamp' },
  { display: 'Last Seen', field: 'last_seen', sortable: true, type: 'timestamp' },
  { display: 'Sparkline', field: 'sparkline', sortable: false, type: 'sparkline', toolTip: 'This is a near term history of the alert being fired, over the previous 24 hours.' },
  { display: 'Source', field: 'source', sortable: true, type: 'text', toolTip: 'What system source Phen developed the alert from.' },
  { display: 'ID', field: 'id', sortable: true, type: 'chip' },
  { display: 'Alert Message', field: 'message', sortable: true, type: 'truncated', toolTip: 'Quick description of the alert that Phen discovered.' },
  { display: 'Phen.AI Ignore', field: 'phen_ignore', sortable: true, type: 'switch' },
  { display: 'Reference', field: 'reference', sortable: false, type: 'link', toolTip: 'Links to specific artifacts and more data about the alert.' },
];

export default UniversalDataTable;